import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useBlocker } from 'react-router';

import { Modal, ModalContent, ModalFooter, ModalHeader } from 'app/components/shared/Modal';

const UnsavedChangesGuard = ({ hasUnsavedChanges }) => {
  // Intercepts in-app navigation: <Link> clicks, programmatic navigate(), and browser back/forward.
  // useBlocker needs the data router, which ApplicationRouter provides on the client (CORE-78999).
  // It replaced history.block() plus unstable_HistoryRouter and UNSAFE_NavigationContext: the
  // blocker is a predicate the router asks rather than an interceptor that has to be dismantled and
  // rebuilt to let a transition through, so there is no arm/re-arm cycle here any more.
  //
  // Navigating to where we already are -- a query-string update, say -- is not a departure.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname
  );

  const isModalOpen = blocker.state === 'blocked';

  // Guards a reload, tab close, or navigation away from the app entirely. Outside the router's
  // reach, so this half is unchanged.
  useEffect(() => {
    if (!hasUnsavedChanges) {
      return undefined;
    }

    const handler = event => {
      event.preventDefault();
      event.returnValue = true;
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);

  // proceed() replays the transition the router held back, including a back/forward step -- so a
  // confirmed Back really goes back, rather than pushing the destination as the previous
  // implementation had to (CORE-78990). Verified in a browser; jsdom does not deliver the popstate
  // this depends on, which is why the spec for it drives a real history instance.
  const handleConfirm = () => {
    if (blocker.proceed) {
      blocker.proceed();
    }
  };

  const closeModal = () => {
    if (blocker.reset) {
      blocker.reset();
    }
  };

  return (
    <Modal handleClose={closeModal} key="ConfirmUnsavedChangesModal" open={isModalOpen} skipHistoryEntry>
      <ModalHeader title="Unsaved changes" />
      <ModalContent>
        <p>
          Are you sure you want to go back? You have unsaved changes on this page that will be lost if you go back
          without saving.
        </p>
      </ModalContent>
      <ModalFooter
        handlePrimaryAction={handleConfirm}
        handleSecondaryAction={closeModal}
        primaryActionButtonText="Yes, go back"
      />
    </Modal>
  );
};

UnsavedChangesGuard.propTypes = {
  hasUnsavedChanges: PropTypes.bool.isRequired
};

/**
 * Client-only, and not by preference: useBlocker exists only under a data router, and the server
 * renders through StaticRouter (see ApplicationRouter). Calling it during SSR throws "useBlocker
 * must be used within a data router", which surfaces as a 500 on a direct load or a refresh of the
 * pages that mount this -- client-side navigation to them is unaffected, which is what makes the
 * failure easy to miss. There is nothing to guard on the server in any case: no history, no
 * navigation, no unsaved edits.
 *
 * The split is what keeps the hooks unconditional; guarding inside the component would break the
 * rules of hooks. Server output is empty either way -- the modal is closed on first render and
 * portals render nothing during SSR -- so hydration has nothing to reconcile.
 */
const ConfirmUnsavedChangesModal = ({ hasUnsavedChanges }) => {
  if (__SERVER__) {
    return null;
  }

  return <UnsavedChangesGuard hasUnsavedChanges={hasUnsavedChanges} />;
};

ConfirmUnsavedChangesModal.propTypes = {
  hasUnsavedChanges: PropTypes.bool.isRequired
};

export default ConfirmUnsavedChangesModal;
